import {
  useCallback, useContext, useMemo,
} from 'react';
import {
  Conversation,
  ConversationAttributes,
  VideoMessage,
  VideoCallMessageType,
  ConversationType,
  selectTwilioUserEntities,
  MessageType,
  TwilioMessageAttributes,
  setChatAlert,
  selectTwilioClient,
  GroupChatType,
  haveAllConversationsLoaded,
} from 'models/twilio';
import {
  batch, useDispatch, useSelector,
} from 'react-redux';
import { useTranslation } from 'react-i18next';
import last from 'lodash/last';
import isEmpty from 'lodash/isEmpty';
import generateVideoCallLink from 'utils/generateVideoCallLink';
import acceptVideoCallInvitation from 'network/acceptVideoCallInvitation';
import createVideoCallRoom from 'network/createVideoCallRoom';
import createGroupVideoCallRoom from 'network/createGroupVideoCallRoom';
import quitVideoCallRoom from 'network/quitVideoCallRoom';
import { GraphQLImService } from 'network/graphql-models';
import { toTwilioUser } from 'models/twilio/converters';
import { setCallStatus } from 'models/rtcCallingStatus';
import { setCallInfo } from 'models/rtcCallingInfo';
import { setWaitingTimeLeft as waitingTimeSetter, setRtcAlert as rtcAlertSetter, resetTimer } from 'models/rtcGlobals';
import { selectLocale } from 'models/localization';
import isVideoRTCSupported from 'utils/isVideoRTCSupported';
import { UserSessionContext } from 'components/UserSession';
import useCurrentAttendee from './useCurrentAttendee';
import { RONGCLOUD_CONVTYPES } from '../constants';
import useMutableRef from './useMutableRef';
import ConverastionContext from '../ConversationContext';
import TRANSLATIONS from '../translations';
import useTwilioEventFunctions from './useTwilioEventFunctions';

const pushVideoMessage = async (conversation: Conversation, message: VideoMessage) => {
  const attributes = conversation.attributes as ConversationAttributes;
  const newVideoAttributes = [...(attributes?.video || []), message];
  await conversation.updateAttributes({
    ...attributes,
    video: newVideoAttributes,
  } as ConversationAttributes);
};

const clearVideoMessage = async (conversation: Conversation) => {
  const attributes = conversation.attributes as ConversationAttributes;
  await conversation.updateAttributes({
    ...attributes,
    video: [],
  } as ConversationAttributes);
};

const useTwilioVideoCall = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const client = useSelector(selectTwilioClient);
  const { userSession } = useContext(UserSessionContext);
  const {
    exhibitorBoothId, gqlId, name, uuid,
  } = useCurrentAttendee();
  const { joinGroupChat } = useTwilioEventFunctions();
  const locale = useSelector(selectLocale);

  const userEntities = useSelector(selectTwilioUserEntities);
  const userEntitiesRef = useMutableRef(userEntities);

  const { getConversationById, findConversationSid, delayExecution } = useContext(ConverastionContext);

  const rtcCallingStatus = useSelector((state) => state.rtcCallingStatus);
  const callingStatusRef = useMutableRef(rtcCallingStatus);

  const rtcCallingInfo = useSelector((state) => state.rtcCallingInfo);
  const callingInfoRef = useMutableRef(rtcCallingInfo);

  const allConversationsLoadedRef = useMutableRef(useSelector(haveAllConversationsLoaded));

  const setWaitingTimeLeft = useCallback((time: number) => {
    dispatch(waitingTimeSetter(time));
  }, [dispatch]);

  const setRtcAlert = useCallback((alert: string) => {
    dispatch(rtcAlertSetter(alert));
  }, [dispatch]);

  const resetWaitingTime = useCallback(() => {
    dispatch(resetTimer());
  }, [dispatch]);

  const resetCallInfo = useCallback(() => {
    batch(() => {
      dispatch(setCallStatus('available'));
      dispatch(setCallInfo({}));
    });
  }, [dispatch]);

  const initCallModal = useCallback((roomName: string) => {
    batch(() => {
      dispatch(setCallInfo({ name: roomName, caller: uuid }));
      if (!isVideoRTCSupported()) {
        dispatch(setCallStatus('caller_browser_not_support'));
        return;
      }
      dispatch(setCallStatus('waiting_for_response'));
    });
  }, [dispatch, uuid]);

  /**
 * @param conversationId: Twilio Conversation SID
 * @param callInvitationType: Look into RONGCLOUD_CONVTYPES
 * @param roomToken: Unique twilio room token
 * @param isTimeout: If fn call reason is because of timeout
 */
  const hangup = useCallback(async (conversationId: string, callInvitationType: number, roomToken: string, isTimeout = false) => {
    if (isTimeout) {
      dispatch(setCallStatus('timeout'));
    } else {
      resetCallInfo();
      resetWaitingTime();
    }
    if (!conversationId || !roomToken) return;
    const conversation = getConversationById(conversationId);
    const { caller } = callingInfoRef.current as any;
    await quitVideoCallRoom(uuid, roomToken);

    pushVideoMessage(conversation, {
      type: VideoCallMessageType.VideoCallHangup,
      content: {
        targetId: null,
        name: null,
        roomToken,
      },
      dateSent: new Date(),
      author: uuid,
    });
    if (caller === uuid) conversation.sendMessage('call.cancelled', { messageType: MessageType.VideoCall } as TwilioMessageAttributes);
  }, [dispatch, resetCallInfo, uuid, resetWaitingTime, callingInfoRef, getConversationById]);

  /**
   * @description Creates a video call room and returns a token of string | null
   * @param roomName: Name of the room. Name of Chat Target / Name of Group Chat
   * @param converastionId: Unique ID of the Chat / Conversation
   * @param inviteeUuid: Array of User ID(s) to invite
   * @param inviteeType: Look into RONGCLOUD_CONVTYPES
   */
  const createVideoCall = useCallback(async (roomName: string, conversationId: string, inviteeUuid: string, inviteeType: number): Promise<void> => {
    initCallModal(roomName);

    const { success, videoCallRoom: { token, status: roomStatus }, errors } = await createVideoCallRoom(uuid, inviteeUuid, conversationId, GraphQLImService.Twilio);
    window.trackingEventToFirebase(userSession, 'VideoCall', 'Video_Call_Click', roomName);

    if (success) {
      if (isEmpty(callingInfoRef.current)) {
        hangup(conversationId, inviteeType, token);
      } else {
        setWaitingTimeLeft(30000);
        dispatch(setCallInfo({
          name: roomName,
          caller: uuid,
          roomToken: token,
          chatId: conversationId,
          calleeIds: [inviteeUuid],
          conversationType: inviteeType,
          roomStatus,
        }));
        const conversation = getConversationById(conversationId);
        await clearVideoMessage(conversation);
        await pushVideoMessage(conversation, {
          type: VideoCallMessageType.VideoCallInvite,
          content: {
            targetId: uuid,
            name,
            roomToken: token,
          },
          dateSent: new Date(),
          author: uuid,
        });
      }
    } else {
      resetCallInfo();
      setRtcAlert(t(`call.${errors[0].type}`));
      dispatch(setChatAlert({ message: TRANSLATIONS.ERRORS.VIDEO_ROOM_CREATE_FAIL[1], translationKey: TRANSLATIONS.ERRORS.VIDEO_ROOM_CREATE_FAIL[0], severity: 'info' }));
    }
  }, [
    callingInfoRef,
    dispatch,
    getConversationById,
    hangup,
    initCallModal,
    name,
    resetCallInfo,
    setRtcAlert,
    setWaitingTimeLeft,
    t,
    userSession,
    uuid,
  ]);

  /**
   * @param roomName: Name of the Booth
   * @param boothId: Booth ID
   */
  const createGroupVideoCall = useCallback(async (roomName: string, boothId: string) => {
    if (!allConversationsLoadedRef.current) {
      delayExecution(() => createGroupVideoCall(roomName, boothId));
      return;
    }

    initCallModal(roomName);

    await joinGroupChat(boothId, { groupChatType: GroupChatType.Booth, displayInterface: false });

    const conversationSid = findConversationSid(boothId, ConversationType.GROUP);
    const conversation = getConversationById(conversationSid);

    const {
      success, videoCallRoom, errors, roomAdmins, roomMember,
    } = await createGroupVideoCallRoom({
      boothId,
      participantId: gqlId,
      imService: GraphQLImService.Twilio,
      conversationId: conversationSid,
    });

    window.trackingEventToFirebase(userSession, 'VideoCall', 'Video_Call_Click', roomName);
    const { token, status: roomStatus } = videoCallRoom || {};
    const { isInviter = false } = roomMember || {};

    if (success) {
      setWaitingTimeLeft(30000);
      const roomAdminIds = roomAdmins.nodes.map((admin) => admin.uuid);
      dispatch(setCallInfo({
        name: roomName,
        caller: uuid,
        roomAdmins: roomAdminIds,
        roomToken: token,
        conversationType: RONGCLOUD_CONVTYPES.GROUP,
        chatId: conversationSid,
        calleeIds: roomAdminIds,
        roomStatus,
        boothId,
      }));
      switch (roomStatus) {
        case 'pending': {
          await clearVideoMessage(conversation);
          await pushVideoMessage(conversation, {
            type: VideoCallMessageType.VideoCallInvite,
            content: {
              targetId: uuid,
              name,
              roomToken: token,
            },
            dateSent: new Date(),
            author: uuid,
          });
          break;
        }
        case 'in_progress': {
          if (!isInviter) {
            await pushVideoMessage(conversation, {
              type: VideoCallMessageType.VideoCallJoinRequest,
              content: {
                targetId: uuid,
                name,
                roomToken: token,
              },
              dateSent: new Date(),
              author: uuid,
            });
          } else {
            dispatch(setCallStatus('caller_connecting'));
            const videoCallUrl = generateVideoCallLink(token, uuid, locale, boothId);
            window.open(videoCallUrl, '_blank');
          }
          break;
        }
        default: break;
      }
    } else {
      resetCallInfo();
      setRtcAlert(t(`call.${errors[0].type}`));
    }
  }, [
    allConversationsLoadedRef,
    delayExecution,
    dispatch,
    findConversationSid,
    getConversationById,
    gqlId,
    initCallModal,
    joinGroupChat,
    locale,
    name,
    resetCallInfo,
    setRtcAlert,
    setWaitingTimeLeft,
    t,
    userSession,
    uuid,
  ]);

  /**
   * @param conversationId: Twilio Conversation SID
   * @param callInvitationType: Look into RONGCLOUD_CONVTYPES
   * @param roomToken: Unique twilio room token
   */
  const accept = useCallback(async (conversationId: string, callInvitationType: number, roomToken: string) => {
    if (!conversationId || !callInvitationType || !roomToken) return;
    dispatch(setCallStatus('callee_connecting'));
    const { success, errors } = await acceptVideoCallInvitation(uuid, roomToken);
    if (success) {
      window.trackingEventToFirebase(userSession, 'VideoCall', 'VideoCall_ConnectedPage_Enter', conversationId);
      const conversation = getConversationById(conversationId);
      await pushVideoMessage(conversation, {
        type: VideoCallMessageType.VideoCallAccept,
        content: {
          targetId: null,
          name: null,
          roomToken,
        },
        dateSent: new Date(),
        author: uuid,
      });
      const videoCallUrl = generateVideoCallLink(roomToken, uuid, locale, conversation.uniqueName);
      window.open(videoCallUrl, '_blank');
      resetWaitingTime();
    } else {
      setRtcAlert(t(`call.${errors[0].type}`));
      resetCallInfo();
    }
  }, [dispatch, t, locale, resetCallInfo, userSession, uuid, resetWaitingTime, setRtcAlert, getConversationById]);

  const onReceiveInvitation = useCallback(async (conversation: Conversation, message: VideoMessage) => {
    const currentAttributes = await conversation.getAttributes() as ConversationAttributes;
    // If not available, fetch
    // Only new users will be unavailable, so isSelf must be false
    const inviter = userEntitiesRef.current[message.author] ?? toTwilioUser(await client.getUser(message.author), false);
    const fromSelf = inviter.isSelf;
    const conversationType = currentAttributes?.type || ConversationType.UNKNOWN;
    const isPrivateChat = conversationType === ConversationType.PRIVATE;

    // For group video call, only booth representatives can answer.
    const isExhibitorOwnBoothChat = conversationType === ConversationType.GROUP && exhibitorBoothId === conversation.uniqueName;

    if (!fromSelf && (isPrivateChat || isExhibitorOwnBoothChat)) {
      if (isVideoRTCSupported()) {
        dispatch(setCallStatus('being_called'));
        setWaitingTimeLeft(30000);
      } else {
        dispatch(setCallStatus('callee_browser_not_support'));
        quitVideoCallRoom(uuid, message.content.roomToken);
        await pushVideoMessage(conversation, {
          type: VideoCallMessageType.VideoCallNotSupport,
          content: {
            targetId: message.author,
            name: message.content.name,
            roomToken: message.content.roomToken,
          },
          dateSent: new Date(),
          author: uuid,
        });
      }
      dispatch(setCallInfo({
        name: inviter.name || message.author,
        roomToken: message.content.roomToken,
        caller: message.author,
        chatId: conversation.sid,
        conversationType,
      }));
    }
  }, [client, dispatch, exhibitorBoothId, uuid, setWaitingTimeLeft, userEntitiesRef]);

  const onReceiveCallAccept = useCallback(async (conversation: Conversation, message: VideoMessage) => {
    const roomToken = (callingInfoRef.current as any)?.roomToken ?? '';

    if (callingStatusRef.current === 'waiting_for_response' && message.content.roomToken === roomToken) {
      const { success, errors } = await acceptVideoCallInvitation(uuid, roomToken);
      if (success) {
        dispatch(setCallStatus('caller_connecting'));
        const videoCallUrl = generateVideoCallLink(roomToken, uuid, locale, conversation.uniqueName);
        window.open(videoCallUrl, '_blank');
        resetWaitingTime();
      } else {
        setRtcAlert(t(`call.${errors[0].type}`));
        resetCallInfo();
      }
    }
  }, [dispatch, t, locale, uuid, resetCallInfo, resetWaitingTime, setRtcAlert, callingInfoRef, callingStatusRef]);

  const onReceiveCallDecline = useCallback(async (conversation: Conversation, message: VideoMessage, isUnsupportedBrowser = false) => {
    const {
      calleeIds, roomToken, chatId, conversationType, caller, roomStatus,
    } = callingInfoRef.current as any;
    if (roomToken !== message.content.roomToken) return;

    if (calleeIds && roomStatus === 'pending' && conversation.sid === chatId) {
      if (calleeIds.length > 0 && calleeIds.includes(message.author)) {
        if (isUnsupportedBrowser && conversationType === RONGCLOUD_CONVTYPES.PRIVATE) {
          dispatch(setCallStatus('other_side_browser_not_support'));
          quitVideoCallRoom(uuid, roomToken);
        } else {
          const waitingIds = calleeIds.filter((id: string) => id !== message.author);
          if (waitingIds.length > 0) {
            dispatch(setCallInfo({
              ...callingInfoRef.current,
              calleeIds: waitingIds,
            }));
          } else {
            resetCallInfo();
            await conversation.sendMessage('call.declined', { messageType: MessageType.VideoCall });
            quitVideoCallRoom(uuid, roomToken);
          }
        }
      }
    } else if (roomStatus === 'in_progress' || (callingStatusRef.current === 'being_called' && caller === message.author)) {
      resetCallInfo();
      quitVideoCallRoom(uuid, roomToken);
    }
  }, [dispatch, resetCallInfo, uuid, callingInfoRef, callingStatusRef]);

  const onReceiveJoinApprove = useCallback(async (conversation: Conversation, message: VideoMessage) => {
    const { content: { roomToken: messageToken, targetId }, author } = message;
    const { roomToken, roomAdmins } = callingInfoRef.current as any;
    if (roomToken === messageToken && roomAdmins?.includes(author) && targetId === uuid) {
      const { success, errors } = await acceptVideoCallInvitation(uuid, roomToken);
      if (success) {
        dispatch(setCallStatus('caller_connecting'));
        const videoCallUrl = generateVideoCallLink(roomToken, uuid, locale, conversation.uniqueName);
        window.open(videoCallUrl, '_blank');
      } else {
        setRtcAlert(t(`call.${errors[0].type}`));
        resetCallInfo();
      }
      resetTimer();
    }
  }, [callingInfoRef, dispatch, locale, setRtcAlert, resetCallInfo, t, uuid]);

  const onReceiveCallSummary = useCallback(async (conversation: Conversation, message: VideoMessage) => {
    const {
      roomToken,
    } = callingInfoRef.current as any;
    const callStatus = callingStatusRef.current;
    if (roomToken !== message.content.roomToken) return;
    if (['caller_connecting', 'callee_connecting'].includes(callStatus)) {
      const { type } = conversation.attributes as ConversationAttributes;
      const { author } = message;
      switch (type) {
        case ConversationType.PRIVATE: {
          resetCallInfo();
          break;
        }
        case ConversationType.GROUP: {
          if (uuid === author) {
            resetCallInfo();
          }
          break;
        }
        default:
          break;
      }
    }
  }, [resetCallInfo, callingInfoRef, callingStatusRef, uuid]);

  const onAttributeUpdatedHandler = useCallback(async (conversation: Conversation) => {
    const attributes = conversation.attributes as ConversationAttributes;
    if (attributes?.video?.length) {
      const latestMessage = last(attributes.video);
      const videoCallActionHandler: Record<VideoCallMessageType, () => Promise<void>> = {
        [VideoCallMessageType.VideoCallInvite]: () => onReceiveInvitation(conversation, latestMessage),
        [VideoCallMessageType.VideoCallAccept]: () => onReceiveCallAccept(conversation, latestMessage),
        [VideoCallMessageType.VideoCallHangup]: () => onReceiveCallDecline(conversation, latestMessage),
        [VideoCallMessageType.VideoCallNotSupport]: () => onReceiveCallDecline(conversation, latestMessage, true),
        [VideoCallMessageType.VideoCallJoinApprove]: () => onReceiveJoinApprove(conversation, latestMessage),
        [VideoCallMessageType.VideoCallJoinRequest]: async () => {},
        [VideoCallMessageType.VideoCallSummary]: () => onReceiveCallSummary(conversation, latestMessage),
      };

      videoCallActionHandler[latestMessage.type]();
    }
  }, [onReceiveInvitation, onReceiveCallAccept, onReceiveCallDecline, onReceiveJoinApprove, onReceiveCallSummary]);

  return useMemo(() => ({
    setRtcAlert,
    accept,
    hangup,
    createVideoCall,
    createGroupVideoCall,
    onAttributeUpdatedHandler,
  }), [
    setRtcAlert,
    accept,
    hangup,
    createVideoCall,
    createGroupVideoCall,
    onAttributeUpdatedHandler,
  ]);
};

export default useTwilioVideoCall;
